"use client";

import React, { useState, useRef } from "react";
import {
  CloudArrowUpIcon,
  QuestionMarkCircleIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { ModalData } from "@/app/(workspace)/_hooks/useModal";
import { HelpModal } from "./HelpModal";

interface UploadFileModalProps {
  data: ModalData;
  onClose: () => void;
}

export function UploadFileModal({ data, onClose }: UploadFileModalProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isDragActive, setIsDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [helpKey, setHelpKey] = useState(0);

  const handleHelpOpen = () => {
    setHelpKey((prev) => prev + 1);
    setIsHelpOpen(true);
  };

  const handleHelpClose = () => {
    setIsHelpOpen(false);
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setIsDragActive(true);
    } else if (e.type === "dragleave") {
      setIsDragActive(false);
    }
  };

  const validateFile = (file: File): boolean => {
    // 파일 확장자 및 크기 검사
    const fileName = file.name.toLowerCase();
    const validExtensions = ['.zip', '.mp4', '.mov', '.webm', '.avi'];
    const hasValidExtension = validExtensions.some(ext => fileName.endsWith(ext));

    if (!hasValidExtension) {
      setError(
        `"${file.name}" is not a valid file. Please select a .zip or video file (.mp4, .mov, .webm, .avi).`
      );
      return false;
    }
    if (file.size > 5 * 1024 * 1024 * 1024) {
      setError(
        `"${file.name}" exceeds the maximum size limit of 5GB. Please select a smaller file.`
      );
      return false;
    }
    if (file.size === 0) {
      setError(`"${file.name}" is empty. Please select a valid file.`);
      return false;
    }
    return true;
  };

  const processFile = (file: File) => {
    setError(null);

    if (!validateFile(file)) {
      return;
    }

    // 유효한 파일인 경우 바로 업로드 진행
    data.onConfirm?.(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    if (e.dataTransfer?.files && e.dataTransfer.files[0]) {
      processFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      processFile(e.target.files[0]);
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="bg-white dark:bg-zinc-900">
      {/* Upload Area */}
      <div className="p-6 sm:p-8 select-none">
        <div
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={handleClick}
          className={`
            relative border-2 border-dashed rounded-xl p-8 sm:p-12 text-center cursor-pointer transition-all
            ${error
              ? "border-red-500 dark:border-red-400 bg-red-50 dark:bg-red-500/10"
              : isDragActive
                ? "border-emerald-500 dark:border-emerald-400 bg-emerald-50 dark:bg-emerald-500/10 ring-4 ring-emerald-500/10 dark:ring-emerald-400/20"
                : "border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800/50 hover:border-emerald-400 dark:hover:border-emerald-500 hover:bg-emerald-50/50 dark:hover:bg-emerald-500/5"
            }
          `}
        >
          {/* Icon */}
          <div className="mb-6">
            {error ? (
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 dark:bg-red-500/20 text-red-600 dark:text-red-400">
                <ExclamationTriangleIcon className="w-8 h-8" />
              </div>
            ) : (
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400">
                <CloudArrowUpIcon className="w-8 h-8" />
              </div>
            )}
          </div>

          {/* Text */}
          <div className="space-y-2">
            {error ? (
              <>
                <p className="text-red-900 dark:text-red-300 text-base sm:text-lg font-semibold">
                  Invalid file type
                </p>
                <p className="text-red-700 dark:text-red-400 text-sm">
                  {error}
                </p>
              </>
            ) : (
              <>
                <p className="text-zinc-900 dark:text-zinc-100 text-base sm:text-lg font-medium">
                  {data.message ||
                    "ZIP 파일을 여기에 드롭하거나 클릭하여 선택하세요"}
                </p>
                <p className="text-zinc-600 dark:text-zinc-400 text-sm">
                  Supported Formats: .zip, .mp4, .mov, .webm, .avi
                </p>
              </>
            )}
          </div>

          {/* Hidden File Input */}
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            onChange={handleFileSelect}
            accept=".zip,.mp4,.mov,.webm,.avi"
          />
        </div>
      </div>

      {/* Bottom Buttons */}
      <div className="flex items-center justify-between px-6 py-4 border-t border-zinc-200 dark:border-zinc-800 select-none">
        <button
          onClick={handleHelpOpen}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-zinc-600 dark:text-zinc-400 hover:text-emerald-600 dark:hover:text-emerald-400 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
        >
          <QuestionMarkCircleIcon className="w-4 h-4" />
          Help
        </button>

        <button
          onClick={onClose}
          className="px-4 py-2 text-sm font-medium text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
        >
          Close
        </button>
      </div>

      {isHelpOpen && (
        <HelpModal
          key={helpKey}
          isOpen={isHelpOpen}
          onClose={handleHelpClose}
        />
      )}
    </div>
  );
}
